import Sidebar from "../components/Sidebar";
import StatCard from "../components/StatCard";
import ContestTable from "../components/ContestTable";
import {
  FaTrophy,
  FaStar,
  FaMedal,
  FaChartLine,
  FaDollarSign,
} from "react-icons/fa";
import Topbar from "../components/Topbar";
import { useEffect, useState } from "react";
import { API_BASE } from "../utils/Instance";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const defaultStats = [
  { label: "Contests Joined", value: 0, icon: <FaChartLine size={20} /> },
  { label: "Best Finish", value: "-", icon: <FaTrophy size={20} /> },
  { label: "Top 3 Finishes", value: 0, icon: <FaMedal size={20} /> },
  { label: "Top 10 Finishes", value: 0, icon: <FaStar size={20} /> },
  { label: "Total Earnings", value: "$0", icon: <FaDollarSign size={20} /> },
];

export default function DashboardCreator() {
  const [contests, setContests] = useState([]);
  const [active, setActive] = useState([]);
  const [stats, setStats] = useState(defaultStats);
  const [loading, setLoading] = useState(true);
  const { profile, refreshAccessToken, logout } = useAuth();
  const navigate = useNavigate();

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
  };

  // Helper function to determine contest status
  const getContestStatus = (startTime, endTime) => {
    const now = new Date();
    const start = new Date(startTime);
    const end = new Date(endTime);

    if (now >= end) return "Ended";
    if (now >= start && now < end) return "In Progress";
    return "Open";
  };

  // Transform API data to match ContestTable format
  const transformContestData = (contest) => {
    const status = getContestStatus(contest.start_time, contest.end_time);
    console.log(`Contest "${contest.title}": status=${status}, start=${contest.start_time}, end=${contest.end_time}, is_active=${contest.is_active}`);
    
    return {
      id: contest.id,
      name: contest.title,
      status: status,
      startDate: formatDate(contest.start_time),
      endDate: formatDate(contest.end_time),
      maxEntrants: contest.max_participants,
      category: contest.category,
      cost: contest.cost,
      estimated_prize: contest.estimated_prize,
      user_entries_count: contest.user_entries_count,
      recurring: contest.recurring,
      joined: contest.joined,
      is_active: contest.is_active,
    };
  };

  // Helper function to make authenticated API calls with token refresh
  const authenticatedFetch = async (url, options = {}) => {
    const token = profile?.accessToken;
    
    if (!token) {
      logout();
      navigate("/login");
      return null;
    }

    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    };

    let response = await fetch(url, { ...options, headers });

    // If 401, try to refresh token and retry
    if (response.status === 401) {
      console.log("Token expired, attempting to refresh...");
      const newToken = await refreshAccessToken();
      
      if (newToken) {
        // Retry with new token
        headers.Authorization = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers });
      } else {
        // Refresh failed, redirect to login
        navigate("/login");
        return null;
      }
    }

    return response;
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch stats
      const statsResponse = await authenticatedFetch(`${API_BASE}dashboard/contributor/`);
      
        if (statsResponse && statsResponse.ok) {
          const statsData = await statsResponse.json();
          setStats([
            { label: "Contests Joined", value: statsData.contests_joined || 0, icon: <FaChartLine size={20} /> },
            { label: "Best Finish", value: statsData.best_finish || "-", icon: <FaTrophy size={20} /> },
            { label: "Top 3 Finishes", value: statsData.top_3_finishes || 0, icon: <FaMedal size={20} /> },
            { label: "Top 10 Finishes", value: statsData.top_10_finishes || 0, icon: <FaStar size={20} /> },
            { label: "Total Earnings", value: `$${Number(statsData.total_earnings ?? 0).toFixed(2)}`, icon: <FaDollarSign size={20} /> },
          ]);
        }

      // Fetch user's joined contests first
      const myContestsResponse = await authenticatedFetch(`${API_BASE}accounts/contests/my-contests/`);
      let joinedIds = [];

      if (myContestsResponse && myContestsResponse.ok) {
        const myContestsData = await myContestsResponse.json();
        console.log('Dashboard - My joined contests:', myContestsData);
        
        const transformedMyContests = myContestsData.map(transformContestData);
        
        // Sort by start date; only show non-ended contests in the active section
        const sortedMyContests = transformedMyContests
          .filter(c => c.status !== "Ended")
          .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
        
        console.log('Dashboard - Transformed my contests:', sortedMyContests);
        setActive(sortedMyContests);
        
        // Extract IDs of joined contests
        joinedIds = myContestsData.map(c => c.id);
        
        // Update "Contests Joined" stat with real count
        setStats(prevStats => {
          const updatedStats = [...prevStats];
          updatedStats[0] = {
            ...updatedStats[0],
            value: myContestsData.length // Real count from database
          };
          return updatedStats;
        });
      }

      // Fetch all contests for "Contests Starting Soon"
      const contestsResponse = await authenticatedFetch(`${API_BASE}accounts/contests/?t=${Date.now()}`);

      if (contestsResponse && contestsResponse.ok) {
        const contestsData = await contestsResponse.json();
        console.log('Dashboard - All contests data:', contestsData);
        
        const transformedContests = contestsData
          // Exclude contests already joined by this contributor
          .filter(c => !joinedIds.includes(c.id))
          .map(transformContestData);
        
        // Filter contests starting soon / not finished
        const upcomingContests = transformedContests
          .filter(c => c.status === "Open" || c.status === "In Progress")
          .sort((a, b) => new Date(a.startDate) - new Date(b.startDate)); // Sort by start date
        
        console.log('Dashboard - Upcoming contests (excluding joined):', upcomingContests);
        setContests(upcomingContests);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Callback to refresh data after joining a contest
  const handleJoinSuccess = async (contestId) => {
    console.log('Contest joined successfully, contest ID:', contestId);
    
    // Remove contest from "Contests Starting Soon" list
    setContests(prev => prev.filter(c => c.id !== contestId));
    
    // Increment "Contests Joined" stat
    setStats(prevStats => {
      const updatedStats = [...prevStats];
      updatedStats[0] = {
        ...updatedStats[0],
        value: updatedStats[0].value + 1 // Increment count
      };
      return updatedStats;
    });
    
    // Refresh "Your Active Contests" section to show the newly joined contest
    try {
      const myContestsResponse = await authenticatedFetch(`${API_BASE}accounts/contests/my-contests/`);
      if (myContestsResponse && myContestsResponse.ok) {
        const myContestsData = await myContestsResponse.json();
        const transformedMyContests = myContestsData.map(transformContestData);
        const sortedMyContests = transformedMyContests
          .filter(c => c.status !== "Ended")
          .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
        setActive(sortedMyContests);
      }
    } catch (error) {
      console.error('Error refreshing active contests:', error);
    }
  };

  useEffect(() => {
    if (!profile?.accessToken) {
      setLoading(false);
      navigate("/login");
      return;
    }

    fetchDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.accessToken]);

  return (
    <div className="flex min-h-screen bg-black">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 lg:ml-64 w-full">
        <Topbar pageTitle="Dashboard" />

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6 md:mb-10">
          {stats.map((s, i) => (
            <StatCard key={i} {...s} />
          ))}
        </div>

        {/* Contests Starting Soon */}
        <h2 className="text-xl font-semibold text-white mb-4">Contests Starting Soon</h2>
        <div className="mb-10 overflow-x-auto">
          {loading ? (
            <div className="text-center text-white py-10">Loading contests...</div>
          ) : contests.length === 0 ? (
            <div className="text-center text-gray-400 py-10">No upcoming contests available.</div>
          ) : (
            <ContestTable
              data={contests}
              setContests={setContests}
              showJoinButton={true}
              onJoinSuccess={handleJoinSuccess}
            />
          )}
        </div>

        {/* Your Active Contests */}
        <h2 className="text-xl font-semibold text-white mb-4">Your Active Contests</h2>
        <div className="overflow-x-auto mb-10">
          {loading ? (
            <div className="text-center text-white py-10">Loading contests...</div>
          ) : active.length === 0 ? (
            <div className="text-center text-gray-400 py-10">No active contests. Join a contest to get started!</div>
          ) : (
          <ContestTable
            data={active}
            setContests={setActive}
            showInProgress={true}
              onJoinSuccess={handleJoinSuccess}
          />
          )}
        </div>
      </div>
    </div>
  );
}
